import * as privateApi from "@/api/privateApi";
import { alternateLinks, rootCanonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { storeSignupContext } from "@/events/signupTracking";
import { trackUpgrade } from "@/events/subscriptionMixpanel";
import { cn } from "@/lib/utils";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import {
  getUserSelector,
  hasSubSelector,
  setBuyIntent,
  setPostLoginRedirect,
  userCurrencySelector,
  userPricesSelector,
} from "@/redux/user/userSlice";
import {
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import { dailyLimitSelector } from "@repo/shared/redux";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { CreditsExplainer } from "./CreditsExplainer";
import { FreePlan } from "./FreePlan";
import { UltimatePlan } from "./UltimatePlan";

/*
  The pricing page for the credits model: one plan, not three (DEV-3210).

  Construction differs from the legacy page deliberately. Those pricing cards
  are light slabs sitting on a dark ground, which is what dates them — they read
  as a widget pasted onto the page. The credits explainer underneath is built
  the other way round, as a panel *of* the ground defined by a hairline border
  and a radius. This applies that upward so both halves read as one page.

  Content: with nothing to compare against, a row of cards is the wrong shape.
  This is a single offer, so price and commitment sit on one side and what you
  get on the other.
*/

type LinkState = {
  from: string;
};

const UltimateSubscriptionPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("subscription");
  // Yearly by default for the FC 27 launch window, when committing for a year
  // is an easier ask. Was monthly; revisit once launch traffic settles.
  const [yearly, setYearly] = useState(true);
  const [loading, setLoading] = useState(false);
  const prices = useSelector(userPricesSelector);
  const currency = useSelector(userCurrencySelector);
  const hasSub = useSelector(hasSubSelector);
  const { user } = useSelector(getUserSelector);
  const isLoggedIn = !!user.uuid;
  const hasUltimateSub = user.subscription === "diamond";
  const planRef = useRef<HTMLDivElement>(null);
  const creditsRef = useRef<HTMLDivElement>(null);
  const { state } = useLocation();
  const from = (state as LinkState)?.from;
  const dailyLimit = useSelector(dailyLimitSelector);

  const live = prices?.find(
    (p) =>
      p.subscription === "diamond" &&
      p.interval === (yearly ? "yearly" : "monthly"),
  );

  useEffect(() => {
    mixpanelTrack("landed_on_subscription_page", { currency });
    // `from` arrives from whatever linked here, and the target may not be
    // mounted — never assume the ref is there.
    if (from === "credits") {
      creditsRef.current?.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
  }, [from]);

  /*
    One plan, so there is no tier to select first — the button goes straight to
    the checkout for whichever billing period the toggle is on. Same three
    outcomes as the legacy page: manage an existing subscription, check out, or
    sign in first with the intent remembered.
  */
  const handleGetUltimate = async () => {
    trackUpgrade("Hero");
    const priceId = live?.id;

    if (!isLoggedIn) {
      mixpanelTrack("goes_to_checkout", {
        currency,
        subscription: "Ultimate",
        subscriptionInterval: yearly ? "yearly" : "monthly",
      });
      dispatch(showModal({ modalType: "sign-in-to-subscribe" }));
      dispatch(setBuyIntent({ subscriptionId: priceId }));
      return;
    }

    setLoading(true);
    try {
      if (hasSub) {
        await privateApi.createCustomerPortalSession();
      } else {
        mixpanelTrack("goes_to_checkout", {
          currency,
          subscription: "Ultimate",
          subscriptionInterval: yearly ? "yearly" : "monthly",
        });
        await privateApi.createCheckoutSession({ priceId, freeTrial: 0 });
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      dispatch(
        addAlert({
          description: errorMessage(backendErrorCode(error)),
          type: "error",
        }),
      );
    }
  };

  return (
    <main className="relative flex flex-col items-center pb-6">
      <Helmet>
        <title>{t("pageTitle", { game: GAME_NAME })}</title>
        <meta name="description" content={t("metaDescription")} />
        <link rel="canonical" href={rootCanonicalUrl("/subscription")} />
        {alternateLinks(rootCanonicalUrl("/subscription"))}
      </Helmet>

      {/*
        The legacy page offsets this by the nav height because its own <main> is
        not positioned, so the gradient hangs off an ancestor that starts at the
        top of the document. This <main> is relative, so the offset would open a
        black strip under the nav instead — inset-0 fills the section exactly.
      */}
      <div className="absolute inset-0 -z-10 bg-linear-to-b from-[#1C2A37] to-[#141414]" />

      <header className="flex w-full max-w-[760px] flex-col items-center px-4 pb-9 pt-10 text-center touch-device:pt-16">
        {/*
          The offer as a fair deal: start at no cost, pay only once the product
          has earned it. The subheading deliberately doesn't name "Ultimate" —
          a visitor reading this has no idea what that is yet, and the plan
          below introduces itself.
        */}
        <h1 className="text-2xl font-semibold leading-tight text-white text-balance touch-device:text-4xl">
          {t("ultimatePage.headline")}
        </h1>
        <p className="mt-3 max-w-[540px] text-sm text-foreground-muted touch-device:text-base">
          {t("ultimatePage.subheadline")}
        </p>

        {/*
          Two segments rather than a switch flanked by labels: each period is
          its own target, the selected one is a raised pill, and the offer rides
          inside the Yearly segment so reaching for "4 months free" and
          reaching for Yearly are the same click.
        */}
        <SegmentedControl
          label={t("billing.period")}
          className="mt-7"
          value={yearly ? "yearly" : "monthly"}
          onChange={(period) => setYearly(period === "yearly")}
          options={[
            { value: "monthly", label: t("billing.monthly") },
            {
              value: "yearly",
              label: (
                <>
                  {t("billing.yearly")}
                  <span className="text-xs font-semibold text-primary-blue">
                    {t("billing.fourMonthsFree")}
                  </span>
                </>
              ),
            },
          ]}
        />
      </header>

      <div ref={planRef} className="w-full px-4">
        <UltimatePlan
          monthlyPrice={live?.monthlyPrice}
          yearlyPrice={live?.yearlyPrice}
          yearly={yearly}
          currency={currency}
          activeSubscription={hasUltimateSub}
          loading={loading}
          onGetUltimate={handleGetUltimate}
        />
      </div>

      {/*
        Only for visitors without an account: the headline promises a free
        start, so the page has to say where it is claimed. Everyone else is
        already on it.
      */}
      {!isLoggedIn && (
        <div className="mt-4 w-full px-4">
          <FreePlan
            dailyLimit={dailyLimit}
            onGetStarted={() => {
              storeSignupContext("cta", { section: "Subscription" });
              /*
                Free plan, so there is nothing to buy and nowhere for the
                subscription page to take them next. My Club is where the free
                tier actually starts — the import is the first thing they need
                to do, and everything the bullets just promised runs on it.
              */
              dispatch(setPostLoginRedirect("/my-club"));
              dispatch(showModal({ modalType: "login" }));
            }}
          />
        </div>
      )}

      <div ref={creditsRef} className="mt-4 w-full">
        <CreditsExplainer
          onSeePlans={() =>
            planRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            })
          }
        />
      </div>
    </main>
  );
};

export default UltimateSubscriptionPage;
